const SMTPUTF8_ATEXT = "[a-z0-9!#$%&'*+/=?^_`{|}~\\u0080-\\u{10ffff}-]";
const RFC5322_LOCAL_PART = `(?:${SMTPUTF8_ATEXT}+(?:\\.${SMTPUTF8_ATEXT}+)*|"(?:[^"\\\\\\r\\n]|\\\\[^\\r\\n])*")`;
const DOMAIN_LABEL = '[a-z0-9\\p{L}\\p{N}](?:[a-z0-9\\p{L}\\p{N}\\p{M}-]{0,61}[a-z0-9\\p{L}\\p{N}\\p{M}])?';

const LOCAL_PART_REGEX = new RegExp(`^${RFC5322_LOCAL_PART}$`, 'iu');
const DOMAIN_REGEX = new RegExp(`^(?:${DOMAIN_LABEL}\\.)+${DOMAIN_LABEL}$`, 'iu');
const IPV4_LITERAL_REGEX =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const IPV6_LITERAL_REGEX =
  /^IPv6:(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){7}|(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,6})?::(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,6})?)$/i;

const stripRfcComments = (value: string): string => {
  let result = '';
  let inQuote = false;
  let commentDepth = 0;

  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    const previousChar = i > 0 ? value[i - 1] : '';

    if (commentDepth > 0) {
      if (char === '(' && previousChar !== '\\\\') {
        commentDepth += 1;
      } else if (char === ')' && previousChar !== '\\\\') {
        commentDepth -= 1;
      }
      continue;
    }

    if (char === '"' && previousChar !== '\\\\') {
      inQuote = !inQuote;
      result += char;
      continue;
    }

    if (!inQuote && char === '(') {
      commentDepth = 1;
      continue;
    }

    result += char;
  }

  return commentDepth === 0 ? result : '';
};

const isValidDomainLiteral = (domainPart: string): boolean => {
  if (!domainPart.startsWith('[') || !domainPart.endsWith(']')) {
    return false;
  }

  const literal = domainPart.slice(1, -1);

  return IPV4_LITERAL_REGEX.test(literal) || IPV6_LITERAL_REGEX.test(literal);
};

const findUnquotedAtIndex = (email: string): number => {
  let inQuote = false;

  for (let i = 0; i < email.length; i++) {
    const char = email[i];
    const previousChar = i > 0 ? email[i - 1] : '';

    if (char === '"' && previousChar !== '\\') {
      inQuote = !inQuote;
      continue;
    }

    if (!inQuote && char === '@') {
      return i;
    }
  }

  return -1;
};

export const isRfc5322Email = (value: string): boolean => {
  if (!value) {
    return false;
  }

  const email = stripRfcComments(value);

  if (!email || email !== email.trim()) {
    return false;
  }

  if (email.length > 254) {
    return false;
  }

  const atIndex = findUnquotedAtIndex(email);
  if (atIndex <= 0 || email.indexOf('@', atIndex + 1) !== -1) {
    return false;
  }

  const localPart = email.slice(0, atIndex);
  const domainPart = email.slice(atIndex + 1);

  if (!localPart || !domainPart) {
    return false;
  }

  if (localPart !== localPart.trim() || domainPart !== domainPart.trim()) {
    return false;
  }

  if (localPart.length > 64 || domainPart.length > 255) {
    return false;
  }

  if (!LOCAL_PART_REGEX.test(localPart)) {
    return false;
  }

  if (domainPart.startsWith('[') || domainPart.endsWith(']')) {
    return isValidDomainLiteral(domainPart);
  }

  return DOMAIN_REGEX.test(domainPart);
};
